import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Form, Space, message, Spin } from 'antd';
import {
  ArrowLeftOutlined, ArrowRightOutlined, ReloadOutlined,
  SaveOutlined, CloseOutlined
} from '@ant-design/icons';

import ProductStudioStepper from './components/ProductStudioStepper';
import ProductStudioSummary from './components/ProductStudioSummary';
import BasicIdentityStep      from './components/steps/BasicIdentityStep';
import ClassificationUsageStep from './components/steps/ClassificationUsageStep';
import GstPricingStep         from './components/steps/GstPricingStep';
import TechnicalDetailsStep   from './components/steps/TechnicalDetailsStep';
import EcommerceProfileStep   from './components/steps/EcommerceProfileStep';
import ItemImagesStep         from './components/steps/ItemImagesStep';
import ReviewSaveStep         from './components/steps/ReviewSaveStep';

// Stage 6B.8A-3B — mode-routing components.
import ProductStudioEntryScreen from './components/ProductStudioEntryScreen';
import ProductQuickCreateForm   from './components/ProductQuickCreateForm';
import ProductGuidedCreateFlow, { GUIDED_STEPS as GUIDED_STEP_DEFS } from './components/ProductGuidedCreateFlow';

import { itemsApi } from './itemsApi';
import {
  STUDIO_STEPS, STUDIO_STEP_KEYS, inferEnabledSections,
  buildCoreItemPayload, buildGlassSpecPayload, buildProfileSpecPayload,
  buildHardwareSpecPayload, buildJobworkSpecPayload, buildEcommerceProfilePayload
} from './studioConstants';
import {
  getNature, getSmartDefaultsForNature, getEnabledSectionsForNature,
  CREATION_MODES
} from './productStudioModes';

import './ProductCreationStudio.css';

// ─────────────────────────────────────────────────────────────────
// Glass-i — Product Creation Studio (Stage 6B.8A-3).
//
// Single guided surface for creating/editing an inventory item plus
// any combination of its six extensions. Orchestrates:
//   1. Core inventory_items create or update
//   2. Glass / Profile / Hardware / Jobwork extension upserts
//      (only when the user enabled that section)
//   3. Ecommerce product profile upsert (only when enabled)
//   4. Image metadata is managed inline on Step 6 (edit-mode only)
//
// Save orchestration runs from the footer:
//   • Mode 'new':  POST core → enabled extensions (per-section try/catch
//                  so partial failures don't lose the core row).
//   • Mode 'edit': PUT core (if dirty) → enabled extensions.
//
// All extension keys live under flat Form value names with prefixes
// chosen to avoid collisions across extensions (e.g. profile thickness
// is `profileThicknessMm`, hardware thickness is `hwThicknessMm`).
// ─────────────────────────────────────────────────────────────────

const DEFAULT_FORM_VALUES = {
  // Core
  itemType: 'trading_good',
  status: 'active',
  isActive: true,
  decimalPrecision: 3,
  isGoods: true,
  isService: false,
  isStockItem: true,
  isPurchasable: true,
  isSellable: true,
  isManufacturable: false,
  isConsumable: false,
  isJobworkItem: false,
  taxabilityType: 'taxable',
  gstRatePercent: 0,
  cessApplicable: false,
  cessRate: 0,
  purchaseRate: 0,
  salesRate: 0,
  mrp: 0,
  minOrderQty: 0,
  leadTimeDays: 0
};

// Map server item row → flat form values. The server snake_cases some
// fields; the items.mapper.fromRow layer already gives us camelCase.
// We keep this defensive in case a future endpoint returns the raw row.
function flattenForForm(item) {
  if (!item || typeof item !== 'object') return {};
  const out = { ...item };
  // Some legacy paths return rateEffectiveFrom as an ISO string. AntD
  // DatePicker wants a dayjs but is happy with a string when wrapped
  // in defaultValue. We leave it as-is; Form normalises on set.
  return out;
}

// Map the technical-step extension form keys to the matching payloads.
// The reason for the per-section pluck is that Step 4 reuses some
// field names across extensions (thicknessMm, coatingType, finishType,
// brand, modelNo, manufacturerCode, isConsumable, isVisible, ...). We
// disambiguate with deliberate field-name prefixes (profileThicknessMm,
// hwBrand, hwIsConsumable, etc.) and translate them back at save time.
function pluckGlassValues(v) {
  return {
    thicknessMm:        v.thicknessMm,
    glassType:          v.glassType,
    glassFamily:        v.glassFamily,
    glassColor:         v.glassColor,
    tintColor:          v.tintColor,
    surfaceType:        v.surfaceType,
    coatingType:        v.coatingType,
    polishTypeId:       v.polishTypeId,
    edgeWorkType:       v.edgeWorkType,
    shapeType:          v.shapeType,
    processType:        v.processType,
    isToughenable:      v.isToughenable,
    isToughened:        v.isToughened,
    isLaminated:        v.isLaminated,
    isDgu:              v.isDgu,
    isHeatStrengthened: v.isHeatStrengthened,
    isBendGlass:        v.isBendGlass,
    remarks:            v.glassRemarks
  };
}

function pluckProfileValues(v) {
  return {
    profileSystem:       v.profileSystem,
    profileMaterial:     v.profileMaterial,
    profileSeries:       v.profileSeries,
    profileCode:         v.profileCode,
    profileName:         v.profileName,
    profileUsage:        v.profileUsage,
    profilePosition:     v.profilePosition,
    profileColor:        v.profileColor,
    finishType:          v.finishType,
    coatingType:         v.coatingType,
    surfaceFinish:       v.surfaceFinish,
    lengthMm:            v.lengthMm,
    sectionWidthMm:      v.sectionWidthMm,
    sectionHeightMm:     v.sectionHeightMm,
    wallThicknessMm:     v.wallThicknessMm,
    thicknessMm:         v.profileThicknessMm,
    weightPerMeter:      v.weightPerMeter,
    brand:               v.brand,
    modelNo:             v.modelNo,
    manufacturerCode:    v.manufacturerCode,
    isAluminium:         v.isAluminium,
    isUpvc:              v.isUpvc,
    isSlimProfile:       v.isSlimProfile,
    isFrameProfile:      v.isFrameProfile,
    isShutterProfile:    v.isShutterProfile,
    isTrackProfile:      v.isTrackProfile,
    isChannelProfile:    v.isChannelProfile,
    isBeadingProfile:    v.isBeadingProfile,
    isPartitionProfile:  v.isPartitionProfile,
    remarks:             v.profileRemarks
  };
}

function pluckHardwareValues(v) {
  return {
    hardwareType:        v.hardwareType,
    hardwareCategory:    v.hardwareCategory,
    hardwareSubcategory: v.hardwareSubcategory,
    hardwareUsage:       v.hardwareUsage,
    hardwarePosition:    v.hardwarePosition,
    hardwareCode:        v.hardwareCode,
    hardwareName:        v.hardwareName,
    material:            v.material,
    finishType:          v.hwFinishType,
    coatingType:         v.hwCoatingType,
    color:               v.color,
    surfaceFinish:       v.surfaceFinish,
    brand:               v.hwBrand,
    modelNo:             v.hwModelNo,
    manufacturerCode:    v.hwManufacturerCode,
    supplierPartNo:      v.supplierPartNo,
    sizeLabel:           v.sizeLabel,
    lengthMm:            v.hwLengthMm,
    widthMm:             v.widthMm,
    heightMm:            v.heightMm,
    diameterMm:          v.diameterMm,
    thicknessMm:         v.hwThicknessMm,
    weightPerUnit:       v.hwWeightPerUnit,
    loadCapacityKg:      v.loadCapacityKg,
    compatibleSystem:    v.compatibleSystem,
    compatibleProfile:   v.compatibleProfile,
    compatibleGlassThicknessMinMm: v.compatibleGlassThicknessMinMm,
    compatibleGlassThicknessMaxMm: v.compatibleGlassThicknessMaxMm,
    isLockable:          v.isLockable,
    isMovable:           v.isMovable,
    isVisible:           v.isVisible,
    isConsumable:        v.hwIsConsumable,
    isFastener:          v.isFastener,
    isGlassHardware:     v.isGlassHardware,
    isProfileHardware:   v.isProfileHardware,
    isShowerHardware:    v.isShowerHardware,
    isDoorHardware:      v.isDoorHardware,
    isWindowHardware:    v.isWindowHardware,
    isFacadeHardware:    v.isFacadeHardware,
    isPartitionHardware: v.isPartitionHardware,
    remarks:             v.hardwareRemarks
  };
}

function pluckJobworkValues(v) {
  return {
    jobworkType:            v.jobworkType,
    jobworkCategory:        v.jobworkCategory,
    jobworkSubcategory:     v.jobworkSubcategory,
    jobworkProcessType:     v.jobworkProcessType,
    jobworkDirection:       v.jobworkDirection,
    jobworkMaterialMode:    v.jobworkMaterialMode,
    jobworkCode:            v.jobworkCode,
    jobworkName:            v.jobworkName,
    chargeBasis:            v.chargeBasis,
    rateBasis:              v.rateBasis,
    minimumCharge:          v.minimumCharge,
    minimumQty:             v.minimumQty,
    wastagePercent:         v.wastagePercent,
    processLossPercent:     v.processLossPercent,
    minThicknessMm:         v.minThicknessMm,
    maxThicknessMm:         v.maxThicknessMm,
    minAreaSqft:            v.minAreaSqft,
    maxAreaSqft:            v.maxAreaSqft,
    minAreaSqm:             v.minAreaSqm,
    maxAreaSqm:             v.maxAreaSqm,
    minLengthMm:            v.minLengthMm,
    maxLengthMm:            v.maxLengthMm,
    minWidthMm:             v.minWidthMm,
    maxWidthMm:             v.maxWidthMm,
    standardLeadTimeDays:        v.standardLeadTimeDays,
    standardProcessTimeMinutes:  v.standardProcessTimeMinutes,
    requiresMachine:        v.requiresMachine,
    requiresQc:             v.requiresQc,
    requiresOutsource:      v.requiresOutsource,
    isInternalProcess:      v.isInternalProcess,
    isCustomerChargeable:   v.isCustomerChargeable,
    isSupplierBillable:     v.isSupplierBillable,
    isInstallationService:  v.isInstallationService,
    isTransportService:     v.isTransportService,
    isLabourService:        v.isLabourService,
    compatibleGlassType:    v.compatibleGlassType,
    compatibleMaterialType: v.compatibleMaterialType,
    compatibleProcessGroup: v.compatibleProcessGroup,
    remarks:                v.jobworkRemarks
  };
}

function pluckEcommerceValues(v) {
  return {
    ecommerceSlug:        v.ecommerceSlug,
    canonicalUrl:         v.canonicalUrl,
    metaTitle:            v.metaTitle,
    metaDescription:      v.metaDescription,
    metaKeywords:         v.metaKeywords,
    ecommerceTitle:       v.ecommerceTitle,
    shortDescription:     v.shortDescription,
    longDescription:      v.longDescription,
    productHighlights:    v.productHighlights,
    technicalSpecs:       v.technicalSpecs,
    careInstructions:     v.careInstructions,
    warrantyText:         v.warrantyText,
    ecommerceCategory:    v.ecommerceCategory,
    ecommerceSubcategory: v.ecommerceSubcategory,
    productCollection:    v.productCollection,
    productTags:          v.productTags,
    publishStatus:        v.publishStatus,
    visibility:           v.visibility,
    isVisibleOnline:      v.isVisibleOnline,
    isFeatured:           v.isFeatured,
    isNewArrival:         v.isNewArrival,
    isBestSeller:         v.isBestSeller,
    isCustomizable:       v.isCustomizable,
    allowOnlineEnquiry:   v.allowOnlineEnquiry,
    allowOnlineOrder:     v.allowOnlineOrder,
    displayOrder:         v.displayOrder,
    badgeText:            v.badgeText,
    searchBoost:          v.searchBoost,
    ogTitle:              v.ogTitle,
    ogDescription:        v.ogDescription,
    ogImageUrl:           v.ogImageUrl
  };
}

// When rendered inside a modal (Item Master in-page edit), the parent
// can pass `itemIdOverride` + `onCloseOverride` so the studio reads
// its target id from props instead of the URL and the Cancel button
// closes the modal instead of navigating away. Direct URL access
// (/erp/masters/items/studio/:id/edit) keeps working unchanged.
export default function ProductCreationStudioPage({
  itemIdOverride = null,
  onCloseOverride = null
} = {}) {
  const { id: paramId } = useParams();
  const routeId = itemIdOverride || paramId || null;
  const navigate = useNavigate();
  const mode = routeId ? 'edit' : 'new';
  const embedded = Boolean(itemIdOverride);
  const handleCancel = onCloseOverride
    ? onCloseOverride
    : () => navigate('/erp/masters/items');

  const [form] = Form.useForm();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [itemId, setItemId] = useState(routeId || null);
  const [enabledSections, setEnabledSections] = useState({
    glass: false, profile: false, hardware: false, jobwork: false, ecommerce: false
  });
  const [imageCount, setImageCount] = useState(0);
  const [saving, setSaving] = useState(false);
  const [loadingItem, setLoadingItem] = useState(false);

  // Watched values used by the summary panel.
  const watchedValues = Form.useWatch([], form) || {};

  // Stage 6B.8A-3B — creation-mode router state.
  //   creationMode: null  → entry screen shown (/new only)
  //                 'quick'   → ProductQuickCreateForm
  //                 'guided'  → 5-step ProductGuidedCreateFlow
  //                 'advanced'→ existing 7-step stepper
  //   itemNature:   one of ITEM_NATURES keys, drives smart defaults
  //                 and which technical section auto-enables.
  // Edit-mode (routeId present) defaults straight to 'advanced' so
  // power users land on the full surface they expect.
  const [creationMode, setCreationMode] = useState(routeId ? 'advanced' : null);
  const [itemNature, setItemNature]     = useState(null);
  const [smartDefaultsApplied, setSmartDefaultsApplied] = useState(null);

  // Initial mount: set defaults; if editing, hydrate from server.
  useEffect(() => {
    form.setFieldsValue(DEFAULT_FORM_VALUES);
  }, [form]);

  // Edit-mode hydrate. Loads core + each extension (404s are ignored —
  // they simply mean the extension hasn't been created yet).
  useEffect(() => {
    let cancelled = false;
    if (mode !== 'edit' || !routeId) return undefined;
    setLoadingItem(true);
    (async () => {
      try {
        const core = await itemsApi.get(routeId);
        if (cancelled) return;
        const flat = flattenForForm(core);
        form.setFieldsValue({ ...DEFAULT_FORM_VALUES, ...flat });
        setItemId(routeId);

        // Lazy-load extensions in parallel.
        const safeGet = async (fn) => {
          try { return await fn(); } catch (e) {
            if (e?.response?.status === 404) return null;
            throw e;
          }
        };
        const [glass, profile, hardware, jobwork, ecom, imgs] = await Promise.all([
          safeGet(() => itemsApi.getGlassSpec(routeId)),
          safeGet(() => itemsApi.getProfileSpec(routeId)),
          safeGet(() => itemsApi.getHardwareSpec(routeId)),
          safeGet(() => itemsApi.getJobworkSpec(routeId)),
          safeGet(() => itemsApi.getEcommerceProfile(routeId)),
          safeGet(() => itemsApi.listImages(routeId))
        ]);
        if (cancelled) return;
        const next = { ...form.getFieldsValue() };

        if (glass) {
          next.thicknessMm     = glass.thicknessMm;
          next.glassType       = glass.glassType;
          next.glassFamily     = glass.glassFamily;
          next.glassColor      = glass.glassColor;
          next.tintColor       = glass.tintColor;
          next.surfaceType     = glass.surfaceType;
          next.coatingType     = glass.coatingType;
          next.polishTypeId    = glass.polishTypeId;
          next.edgeWorkType    = glass.edgeWorkType;
          next.shapeType       = glass.shapeType;
          next.processType     = glass.processType;
          next.isToughenable   = glass.isToughenable;
          next.isToughened     = glass.isToughened;
          next.isLaminated     = glass.isLaminated;
          next.isDgu           = glass.isDgu;
          next.isHeatStrengthened = glass.isHeatStrengthened;
          next.isBendGlass     = glass.isBendGlass;
          next.glassRemarks    = glass.remarks;
        }
        if (profile) {
          next.profileSystem       = profile.profileSystem;
          next.profileMaterial     = profile.profileMaterial;
          next.profileSeries       = profile.profileSeries;
          next.profileCode         = profile.profileCode;
          next.profileName         = profile.profileName;
          next.profileUsage        = profile.profileUsage;
          next.profilePosition     = profile.profilePosition;
          next.profileColor        = profile.profileColor;
          next.finishType          = profile.finishType;
          next.coatingType         = profile.coatingType || next.coatingType;
          next.surfaceFinish       = profile.surfaceFinish;
          next.lengthMm            = profile.lengthMm;
          next.sectionWidthMm      = profile.sectionWidthMm;
          next.sectionHeightMm     = profile.sectionHeightMm;
          next.wallThicknessMm     = profile.wallThicknessMm;
          next.profileThicknessMm  = profile.thicknessMm;
          next.weightPerMeter      = profile.weightPerMeter;
          next.brand               = profile.brand;
          next.modelNo             = profile.modelNo;
          next.manufacturerCode    = profile.manufacturerCode;
          next.isAluminium         = profile.isAluminium;
          next.isUpvc              = profile.isUpvc;
          next.isSlimProfile       = profile.isSlimProfile;
          next.isFrameProfile      = profile.isFrameProfile;
          next.isShutterProfile    = profile.isShutterProfile;
          next.isTrackProfile      = profile.isTrackProfile;
          next.isChannelProfile    = profile.isChannelProfile;
          next.isBeadingProfile    = profile.isBeadingProfile;
          next.isPartitionProfile  = profile.isPartitionProfile;
          next.profileRemarks      = profile.remarks;
        }
        if (hardware) {
          next.hardwareType        = hardware.hardwareType;
          next.hardwareCategory    = hardware.hardwareCategory;
          next.hardwareSubcategory = hardware.hardwareSubcategory;
          next.hardwareUsage       = hardware.hardwareUsage;
          next.hardwarePosition    = hardware.hardwarePosition;
          next.hardwareCode        = hardware.hardwareCode;
          next.hardwareName        = hardware.hardwareName;
          next.material            = hardware.material;
          next.hwFinishType        = hardware.finishType;
          next.hwCoatingType       = hardware.coatingType;
          next.color               = hardware.color;
          next.hwBrand             = hardware.brand;
          next.hwModelNo           = hardware.modelNo;
          next.hwManufacturerCode  = hardware.manufacturerCode;
          next.supplierPartNo      = hardware.supplierPartNo;
          next.sizeLabel           = hardware.sizeLabel;
          next.hwLengthMm          = hardware.lengthMm;
          next.widthMm             = hardware.widthMm;
          next.heightMm            = hardware.heightMm;
          next.diameterMm          = hardware.diameterMm;
          next.hwThicknessMm       = hardware.thicknessMm;
          next.hwWeightPerUnit     = hardware.weightPerUnit;
          next.loadCapacityKg      = hardware.loadCapacityKg;
          next.compatibleSystem    = hardware.compatibleSystem;
          next.compatibleProfile   = hardware.compatibleProfile;
          next.compatibleGlassThicknessMinMm = hardware.compatibleGlassThicknessMinMm;
          next.compatibleGlassThicknessMaxMm = hardware.compatibleGlassThicknessMaxMm;
          next.isLockable          = hardware.isLockable;
          next.isMovable           = hardware.isMovable;
          next.isVisible           = hardware.isVisible;
          next.hwIsConsumable      = hardware.isConsumable;
          next.isFastener          = hardware.isFastener;
          next.isGlassHardware     = hardware.isGlassHardware;
          next.isProfileHardware   = hardware.isProfileHardware;
          next.isShowerHardware    = hardware.isShowerHardware;
          next.isDoorHardware      = hardware.isDoorHardware;
          next.isWindowHardware    = hardware.isWindowHardware;
          next.isFacadeHardware    = hardware.isFacadeHardware;
          next.isPartitionHardware = hardware.isPartitionHardware;
          next.hardwareRemarks     = hardware.remarks;
        }
        if (jobwork) {
          for (const k of Object.keys(jobwork)) {
            if (k === 'remarks') next.jobworkRemarks = jobwork[k];
            else next[k] = jobwork[k];
          }
        }
        if (ecom) {
          for (const k of Object.keys(ecom)) {
            next[k] = ecom[k];
          }
        }
        form.setFieldsValue(next);

        setEnabledSections({
          glass:     Boolean(glass),
          profile:   Boolean(profile),
          hardware:  Boolean(hardware),
          jobwork:   Boolean(jobwork),
          ecommerce: Boolean(ecom)
        });
        const imgRows = imgs && Array.isArray(imgs.rows) ? imgs.rows
          : (Array.isArray(imgs) ? imgs : []);
        setImageCount(imgRows.length);
      } catch (e) {
        const msg = e?.response?.data?.error?.message || 'Failed to load item.';
        message.error(msg);
      } finally {
        if (!cancelled) setLoadingItem(false);
      }
    })();
    return () => { cancelled = true; };
  }, [mode, routeId, form]);

  // When classification flags change, re-infer the section toggles
  // — but only in create mode, and only when the user hasn't manually
  // touched a toggle yet. Once the user enables/disables a section
  // explicitly, the inference no longer fires.
  const [userOverrodeToggles, setUserOverrodeToggles] = useState(false);

  const inferredSnapshot = useMemo(() => inferEnabledSections({
    itemType:         watchedValues.itemType,
    isService:        watchedValues.isService,
    isJobworkItem:    watchedValues.isJobworkItem,
    itemName:         watchedValues.itemName
  }), [watchedValues.itemType, watchedValues.isService,
       watchedValues.isJobworkItem, watchedValues.itemName]);

  useEffect(() => {
    if (mode !== 'new') return;
    if (userOverrodeToggles) return;
    setEnabledSections((prev) => ({ ...prev, ...inferredSnapshot }));
  }, [inferredSnapshot, mode, userOverrodeToggles]);

  const toggleSection = useCallback((key, on) => {
    setUserOverrodeToggles(true);
    setEnabledSections((prev) => ({ ...prev, [key]: Boolean(on) }));
  }, []);

  const toggleEcommerce = useCallback((on) => {
    setUserOverrodeToggles(true);
    setEnabledSections((prev) => ({ ...prev, ecommerce: Boolean(on) }));
  }, []);

  // ── Save orchestration ───────────────────────────────────────
  const saveAll = async () => {
    // Lightweight client-side validation. The backend validates again
    // — this is the polite first pass.
    try {
      await form.validateFields();
    } catch (e) {
      message.error('Please fix the highlighted fields before saving.');
      return;
    }
    const values = form.getFieldsValue();
    if (values.taxabilityType === 'service' || values.taxabilityType === 'rcm') {
      message.error('Taxability cannot be "service" or "rcm" on Item Master.');
      return;
    }
    if (values.isGoods === false && values.isService === false) {
      message.error('At least one of Goods or Service must be on.');
      return;
    }

    const corePayload = buildCoreItemPayload(values);

    setSaving(true);
    try {
      let id = itemId;
      if (mode === 'new' && !id) {
        const created = await itemsApi.create(corePayload);
        id = created && created.id;
        if (!id) throw new Error('Backend did not return the new item id.');
        setItemId(id);
        message.success('Item created. Saving enabled specs…');
      } else if (id) {
        // edit mode: send full core payload — backend stripUnknown leaves
        // the rest of the row alone.
        await itemsApi.update(id, corePayload);
      }

      const failures = [];
      const enabled = enabledSections;

      if (enabled.glass) {
        const payload = buildGlassSpecPayload(pluckGlassValues(values));
        if (Object.keys(payload).length > 0) {
          try { await itemsApi.upsertGlassSpec(id, payload); }
          catch (e) {
            failures.push(['Glass details', e?.response?.data?.error?.message || e.message]);
          }
        }
      }
      if (enabled.profile) {
        const payload = buildProfileSpecPayload(pluckProfileValues(values));
        if (Object.keys(payload).length > 0) {
          try { await itemsApi.upsertProfileSpec(id, payload); }
          catch (e) {
            failures.push(['Profile details', e?.response?.data?.error?.message || e.message]);
          }
        }
      }
      if (enabled.hardware) {
        const payload = buildHardwareSpecPayload(pluckHardwareValues(values));
        if (Object.keys(payload).length > 0) {
          try { await itemsApi.upsertHardwareSpec(id, payload); }
          catch (e) {
            failures.push(['Hardware details', e?.response?.data?.error?.message || e.message]);
          }
        }
      }
      if (enabled.jobwork) {
        const payload = buildJobworkSpecPayload(pluckJobworkValues(values));
        if (Object.keys(payload).length > 0) {
          try { await itemsApi.upsertJobworkSpec(id, payload); }
          catch (e) {
            failures.push(['Jobwork details', e?.response?.data?.error?.message || e.message]);
          }
        }
      }
      if (enabled.ecommerce) {
        const payload = buildEcommerceProfilePayload(pluckEcommerceValues(values));
        if (Object.keys(payload).length > 0) {
          try { await itemsApi.upsertEcommerceProfile(id, payload); }
          catch (e) {
            failures.push(['Ecommerce profile', e?.response?.data?.error?.message || e.message]);
          }
        }
      }

      if (failures.length === 0) {
        message.success('Item saved successfully.');
      } else {
        message.warning(
          `Core saved. Some sections did not save: ${failures.map((f) => f[0]).join(', ')}.`,
          6
        );
      }

      if (mode === 'new' && id && !embedded) {
        navigate(`/erp/masters/items/studio/${id}/edit`, { replace: true });
      }
      if (mode === 'edit' && embedded && onCloseOverride) {
        // In-page modal flow: close on successful save so the grid
        // can refresh. Toast was already raised above.
        onCloseOverride(id);
      }
    } catch (e) {
      const msg = e?.response?.data?.error?.message || e?.message || 'Failed to save item.';
      message.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    form.resetFields();
    form.setFieldsValue(DEFAULT_FORM_VALUES);
    setEnabledSections({
      glass: false, profile: false, hardware: false, jobwork: false, ecommerce: false
    });
    setUserOverrodeToggles(false);
    setCurrentIndex(0);
    message.info('Form reset.');
  };

  // Step-count + last-step rules depend on the active mode. Advanced
  // keeps the full 7 steps; Guided uses the 5-step list defined in
  // productStudioModes.js; Quick has no stepper at all.
  const activeStepCount = creationMode === 'guided'
    ? GUIDED_STEP_DEFS.length
    : STUDIO_STEPS.length;
  const goPrev = () => setCurrentIndex((i) => Math.max(0, i - 1));
  const goNext = () => setCurrentIndex((i) => Math.min(activeStepCount - 1, i + 1));

  const currentStepKey = STUDIO_STEP_KEYS[currentIndex];
  const lastStep = currentIndex === activeStepCount - 1;

  // Stage 6B.8A-3B — Entry screen → form. Called once the user picks
  // a mode + nature on /new. Applies smart defaults onto the shared
  // Form instance + auto-enables the matching extension section.
  const handleEntryStart = useCallback(({ mode: chosenMode, nature: chosenNature }) => {
    setCreationMode(chosenMode);
    setItemNature(chosenNature);
    const defaults = getSmartDefaultsForNature(chosenNature);
    setSmartDefaultsApplied(defaults);
    form.setFieldsValue({ ...DEFAULT_FORM_VALUES, ...defaults });
    setEnabledSections((prev) => ({ ...prev, ...getEnabledSectionsForNature(chosenNature) }));
    setUserOverrodeToggles(true);   // freeze auto-inference; user explicitly chose a nature
    setCurrentIndex(0);
  }, [form]);

  // "Change nature" link in the banner sends the user back to the
  // entry screen. Form values stay so a typo recovery is painless.
  const handleChangeNature = useCallback(() => {
    if (creationMode === 'advanced') return; // advanced mode doesn't have nature
    setCreationMode(null);
    setItemNature(null);
    setSmartDefaultsApplied(null);
    setCurrentIndex(0);
  }, [creationMode]);

  // Switch modes mid-flow without losing what the user has typed.
  const switchMode = useCallback((nextMode) => {
    setCreationMode(nextMode);
    setCurrentIndex(0);
  }, []);

  const sectionStatusMap = useMemo(() => ({
    technical:
      [enabledSections.glass    ? 'Glass'    : null,
       enabledSections.profile  ? 'Profile'  : null,
       enabledSections.hardware ? 'Hardware' : null,
       enabledSections.jobwork  ? 'Jobwork'  : null].filter(Boolean).join(' · ') || 'none on',
    ecommerce: enabledSections.ecommerce ? 'On' : 'Off',
    images: imageCount > 0 ? `${imageCount} image${imageCount === 1 ? '' : 's'}` : (mode === 'edit' ? 'none' : 'save first')
  }), [enabledSections, imageCount, mode]);

  // ── Entry screen — shown only on /new before user picks mode ──
  if (mode === 'new' && creationMode === null) {
    return (
      <div className="erp-product-studio">
        <ProductStudioEntryScreen onStart={handleEntryStart} />
      </div>
    );
  }

  // ── Header / mode strip — shared by Quick / Guided / Advanced ──
  const activeModeLabel = (CREATION_MODES.find((m) => m.key === creationMode) || {}).label
    || 'Advanced Create';
  const natureMeta = getNature(itemNature);

  const studioHeader = (
    <header className="erp-studio-header">
      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <h1>Product Creation Studio</h1>
        <span className="erp-studio-mode-chip">
          {activeModeLabel}
          {natureMeta ? <> · <strong>{natureMeta.label}</strong></> : null}
        </span>
        {mode === 'new' && creationMode !== 'advanced' ? (
          <Button
            size="small"
            type="link"
            onClick={() => switchMode('advanced')}
            style={{ marginLeft: 'auto', padding: 0 }}
          >
            Switch to Advanced
          </Button>
        ) : null}
        {mode === 'new' && creationMode === 'advanced' ? (
          <Button
            size="small"
            type="link"
            onClick={() => switchMode('quick')}
            style={{ marginLeft: 'auto', padding: 0 }}
          >
            Switch to Quick
          </Button>
        ) : null}
      </div>
      <p>
        {mode === 'edit'
          ? `Editing item ${watchedValues.itemCode ? '· ' + watchedValues.itemCode : ''}`
          : (creationMode === 'quick'
              ? 'Just the essentials — perfect for everyday item creation.'
              : creationMode === 'guided'
                ? 'Five short steps. Smart defaults applied based on your item nature.'
                : 'Full control. All core fields plus every extension surface.')}
      </p>
    </header>
  );

  // ── Footer — Quick uses a tight single-button footer; the stepper
  // modes (Guided / Advanced) use the standard Back/Next/Save chain.
  const isStepperMode = creationMode === 'guided' || creationMode === 'advanced' || mode === 'edit';
  const stepLabel = creationMode === 'guided'
    ? `Step ${currentIndex + 1} of ${GUIDED_STEP_DEFS.length} — ${GUIDED_STEP_DEFS[currentIndex].title}`
    : `Step ${currentIndex + 1} of ${STUDIO_STEPS.length} — ${STUDIO_STEPS[currentIndex].title}`;

  return (
    <div className="erp-product-studio">
      {studioHeader}

      <Spin spinning={loadingItem} tip="Loading item…">
        <Form
          form={form}
          layout="vertical"
          requiredMark={false}
          preserve
          autoComplete="off"
        >
          {/* Quick Create — single-card flow, no stepper, no summary */}
          {creationMode === 'quick' && mode === 'new' ? (
            <div style={{ padding: '14px 20px 0' }}>
              <ProductQuickCreateForm
                form={form}
                natureKey={itemNature}
                smartDefaults={smartDefaultsApplied}
                onChangeNature={handleChangeNature}
                disabled={saving}
              />
            </div>
          ) : null}

          {/* Guided Create — 5 steps with reused step components */}
          {creationMode === 'guided' && mode === 'new' ? (
            <ProductGuidedCreateFlow
              form={form}
              currentIndex={currentIndex}
              setCurrentIndex={setCurrentIndex}
              natureKey={itemNature}
              smartDefaults={smartDefaultsApplied}
              onChangeNature={handleChangeNature}
              enabledSections={enabledSections}
              imageCount={imageCount}
              itemId={itemId}
              mode={mode}
              watchedValues={watchedValues}
              saving={saving}
            />
          ) : null}

          {/* Advanced Create + edit mode — existing 7-step stepper */}
          {(creationMode === 'advanced' || mode === 'edit') ? (
            <div className="erp-studio-body">
              <ProductStudioStepper
                currentIndex={currentIndex}
                onJump={setCurrentIndex}
                sectionStatusMap={sectionStatusMap}
              />

              <div className="erp-studio-content">
                {currentStepKey === 'basic' ? (
                  <BasicIdentityStep disabled={saving} />
                ) : null}
                {currentStepKey === 'classification' ? (
                  <ClassificationUsageStep disabled={saving} currentItemId={itemId} />
                ) : null}
                {currentStepKey === 'gst_pricing' ? (
                  <GstPricingStep disabled={saving} />
                ) : null}
                {currentStepKey === 'technical' ? (
                  <TechnicalDetailsStep
                    disabled={saving}
                    enabledSections={enabledSections}
                    onToggleSection={toggleSection}
                  />
                ) : null}
                {currentStepKey === 'ecommerce' ? (
                  <EcommerceProfileStep
                    disabled={saving}
                    ecommerceEnabled={enabledSections.ecommerce}
                    onToggleEcommerce={toggleEcommerce}
                  />
                ) : null}
                {currentStepKey === 'images' ? (
                  <ItemImagesStep
                    disabled={saving}
                    itemId={itemId}
                    onImageCountChange={setImageCount}
                  />
                ) : null}
                {currentStepKey === 'review' ? (
                  <ReviewSaveStep
                    values={watchedValues}
                    enabledSections={enabledSections}
                    imageCount={imageCount}
                    mode={mode}
                    itemId={itemId}
                  />
                ) : null}
              </div>

              <ProductStudioSummary
                values={watchedValues}
                enabledSections={enabledSections}
                imageCount={imageCount}
                mode={mode}
                itemId={itemId}
              />
            </div>
          ) : null}
        </Form>
      </Spin>

      <div className="erp-studio-footer">
        <div className="erp-studio-footer-help">
          {creationMode === 'quick' && mode === 'new'
            ? <span>Quick Create — saving creates the core item and any relevant extension automatically.</span>
            : <span>{stepLabel}</span>}
        </div>
        <Space>
          <Button icon={<CloseOutlined />}
            onClick={handleCancel}
            disabled={saving}>
            Cancel
          </Button>
          <Button icon={<ReloadOutlined />} onClick={handleReset} disabled={saving}>
            Reset
          </Button>
          {isStepperMode ? (
            <>
              <Button
                icon={<ArrowLeftOutlined />}
                onClick={goPrev}
                disabled={currentIndex === 0 || saving}
              >
                Back
              </Button>
              {!lastStep ? (
                <Button type="default" onClick={goNext} disabled={saving}>
                  Next <ArrowRightOutlined />
                </Button>
              ) : null}
            </>
          ) : null}
          <Button
            type="primary"
            icon={<SaveOutlined />}
            loading={saving}
            onClick={saveAll}
          >
            {mode === 'edit' ? 'Save Changes' : 'Create Item'}
          </Button>
        </Space>
      </div>
    </div>
  );
}
